import React from 'react';
import { Icon } from '@repo/ui';
import { Button, Card, CardHeader, CardBody, Select } from '@repo/ui';
import { showToast } from '@repo/ui';
import type { Template, TestForm } from '../types';
import { loadTemplateSampleData, loadJuniperData } from '../utils';

// Standardized button styles
const BUTTON_STYLES = {
  standard: {
    variant: 'outline' as const,
    size: 'sm' as const,
  },
  primary: {
    variant: 'primary' as const,
    size: 'md' as const,
  },
} as const;

interface TestTemplateFormProps {
  testForm: TestForm;
  onTestFormChange: (form: TestForm) => void;
  onTest: () => void;
  systemTemplates: Template[];
  tenantTemplates: Template[];
  isTesting: boolean;
}

export const TestTemplateForm: React.FC<TestTemplateFormProps> = ({
  testForm,
  onTestFormChange,
  onTest,
  systemTemplates,
  tenantTemplates,
  isTesting,
}) => {
  const allTemplates = [...systemTemplates, ...tenantTemplates];

  const handleTemplateSelect = (value: string | string[]) => {
    const templateId = Array.isArray(value) ? value[0] || '' : String(value || '');
    onTestFormChange({ ...testForm, templateId });

    // Load sample data if available
    const selectedTemplate = allTemplates.find(t => t.templateId === templateId);
    if (selectedTemplate?.sampleData) {
      onTestFormChange({
        ...testForm,
        templateId,
        sampleData: JSON.stringify(selectedTemplate.sampleData, null, 2),
      });
      showToast.success('Template selected with sample data loaded!');
    }
  };

  return (
    <div className="flex justify-center">
      <Card className="max-w-2xl w-full">
        <CardHeader>
          <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-dark-50">
            Test Template
          </h2>
          <p className="text-xtnd-dark-500 dark:text-xtnd-dark-300 text-sm">
            Test template rendering with sample JSON data
          </p>
        </CardHeader>
        <CardBody>
          <div className="space-y-4 text-sm font-medium text-xtnd-dark dark:text-xtnd-dark-50">
            <div>
              <label className="block mb-2">Select Template</label>
              <Select
                value={testForm.templateId}
                onChange={handleTemplateSelect}
                options={[
                  { value: '', label: 'Select a template...' },

                  // System templates
                  ...(systemTemplates.length
                    ? systemTemplates.map(t => ({
                        value: t.templateId,
                        label: `🔵 [System Template] ${t.templateName} (${t.templateType}) - v${t.version}`,
                      }))
                    : []),

                  // Tenant templates
                  ...(tenantTemplates.length
                    ? tenantTemplates.map(t => ({
                        value: t.templateId,
                        label: `🟢 ${t.templateName} (${t.templateType}) - v${t.version}`,
                      }))
                    : []),
                ]}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block">Sample JSON Data</label>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    {...BUTTON_STYLES.standard}
                    onClick={() =>
                      loadTemplateSampleData(
                        allTemplates,
                        testForm.templateId,
                        onTestFormChange,
                        showToast
                      )
                    }
                    disabled={!testForm.templateId}
                  >
                    Load Template Sample
                  </Button>
                  <Button
                    type="button"
                    {...BUTTON_STYLES.standard}
                    onClick={() => loadJuniperData(onTestFormChange, showToast)}
                    disabled={!sessionStorage.getItem('juniperData')}
                  >
                    Load Juniper Data
                  </Button>
                </div>
              </div>
              <textarea
                value={testForm.sampleData}
                onChange={e => onTestFormChange({ ...testForm, sampleData: e.target.value })}
                className="w-full h-48 p-3 border border-xtnd-dark-200 rounded-lg font-mono text-sm bg-white dark:bg-xtnd-dark-800 text-xtnd-dark dark:text-xtnd-light"
                placeholder="Enter JSON data for template variables"
              />
              {sessionStorage.getItem('juniperData') && (
                <p className="text-xs text-green-600 mt-1">
                  ✓ Juniper data available in session storage
                </p>
              )}
            </div>

            <div className="flex justify-end">
              <Button
                onClick={onTest}
                disabled={!testForm.templateId || isTesting}
                loading={isTesting}
                {...BUTTON_STYLES.primary}
                leftIcon={<Icon name="HiPlay" className="h-4 w-4" />}
              >
                Test Template
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

